import { api } from '@/lib/api';
import type {
  AdjustmentValues,
  ConsumptionValues,
  ReceiptValues,
  TransferValues,
} from './schema';
import type { StockMovement } from './types';

/**
 * Stock-movements REST client (Phase 4c).
 *
 * Endpoints:
 *   GET  /stock-lots/:id/movements?limit=...   history (read-only)
 *   POST /stock-lots/:id/receipt               receipt (incoming)
 *   POST /stock-lots/:id/consumption           consumption (used)
 *   POST /stock-lots/:id/adjustment            adjustment (signed)
 *   POST /stock-movements/transfer             transfer (paired tx)
 *
 * The four write endpoints all return ONE StockMovement (the
 * primary one, even for transfer which atomically creates two —
 * the backend returns the sourceLotId-side movement). The caller's
 * mutation hook is responsible for invalidating BOTH the affected
 * lot's detail (quantityOnHand changes) AND the movements list.
 * For transfer, both source AND destination lots need invalidation.
 */
export const stockMovementsApi = {
  listForLot: async (
    stockLotId: string,
    limit?: number,
  ): Promise<StockMovement[]> => {
    const { data } = await api.get<StockMovement[]>(
      `/stock-lots/${stockLotId}/movements`,
      { params: limit ? { limit } : undefined },
    );
    return data;
  },

  receipt: async (
    stockLotId: string,
    input: ReceiptValues,
  ): Promise<StockMovement> => {
    const { data } = await api.post<StockMovement>(
      `/stock-lots/${stockLotId}/receipt`,
      input,
    );
    return data;
  },

  consumption: async (
    stockLotId: string,
    input: ConsumptionValues,
  ): Promise<StockMovement> => {
    const { data } = await api.post<StockMovement>(
      `/stock-lots/${stockLotId}/consumption`,
      input,
    );
    return data;
  },

  adjustment: async (
    stockLotId: string,
    input: AdjustmentValues,
  ): Promise<StockMovement> => {
    const { data } = await api.post<StockMovement>(
      `/stock-lots/${stockLotId}/adjustment`,
      input,
    );
    return data;
  },

  transfer: async (input: TransferValues): Promise<StockMovement> => {
    const { data } = await api.post<StockMovement>(
      '/stock-movements/transfer',
      input,
    );
    return data;
  },
};
